'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isProduction } from '../services/env';
import mapFieldsToState from '../lib/mapFieldsToState';
import getVariantsFormFields from '../lib/getVariantsFormFields';
import { formatPriceInput } from '../services/formatPrice';
import getPaymentMethod from '../lib/getPaymentMethod';
import { useQueryClient } from '@tanstack/react-query';
import getSessionId from '../lib/getSessionId';
import { submissionKey, paymentKey } from '../queryKeys';
import { getTicketGroupsFormState } from '../lib/getTicketGroupsFormState';
import getCheckoutPath from '../lib/getCheckoutPath';
import getSellerSlug from '../services/getSellerSlug';
import getCheckoutSlug from '../services/getCheckoutSlug';
import getPaymentOption from '../lib/getPaymentOption';
import useCheckoutForm from './useCheckoutForm';
import useMatchMedia from './useMatchMedia';
import useCheckoutRouter from './useCheckoutRouter';
import { checkoutTypes } from '../enums/checkout-types';

export const checkoutStates = {
  INITIAL: 'initial',
  SUBMITTING: 'submitting',
  CREATING_SUBMISSION: 'creating_submission',
  CREATING_PAYMENT: 'creating_payment',
  CREATING_SUBSCRIPTION: 'creating_subscription',
  PROCESSING: 'processing',
  PROCESSING_PAYMENT: 'processing_payment',
  PROCESSING_SUBSCRIPTION: 'processing_subscription',
  ERROR: 'error',
  PENDING: 'pending',
  COMPLETED: 'completed',
};

export const checkoutLayouts = {
  CENTER: 'center',
  SPLIT: 'split',
  DOUBLE: 'double',
  DOUBLE_BANNER: 'double_banner',
};

function getInitialCheckoutState(query) {
  const { payment_intent, payment_intent_client_secret } = query;

  if (payment_intent && payment_intent_client_secret) {
    return checkoutStates.PROCESSING_PAYMENT;
  }

  return checkoutStates.INITIAL;
}

function getAddressMode({ shippingAddressCollection, billingAddressCollection }) {
  if (shippingAddressCollection) {
    return 'shipping';
  } else if (billingAddressCollection) {
    return 'billing';
  } else {
    return null;
  }
}

function getCheckoutLayout(checkoutLayout, checkoutType) {
  if (checkoutType === checkoutTypes.EMBED) {
    return checkoutLayouts.CENTER;
  } else if (checkoutType === checkoutTypes.CHECKOUT || checkoutType === checkoutTypes.IFRAME) {
    if (checkoutLayout === checkoutLayouts.DOUBLE) {
      return checkoutLayouts.DOUBLE;
    } else {
      return checkoutLayout;
    }
  } else {
    return checkoutLayouts.CENTER;
  }
}

const CheckoutStateContext = React.createContext({});

const CheckoutStateProvider = ({ value = {}, children }) => {
  const {
    checkout = {},
    product = {},
    seller = {},
    options: options = {
      type: checkoutTypes.CHECKOUT,
    },
    upsell = {},
    isUpsell = false,
    isPreview = false,
    isTesting = false,
    isTestingPayments = false,
    isMeasurer = false,
    isScreenshot = false,
    query = {},
    params = {},
    countryCode,
    iframeId,
    isConfirmation = false,
    publicKey,
    publicKeyTest,
    draftCheckout = null,
  } = value;

  const checkoutType = useMemo(() => options.type, [options.type]);

  const isSmall = useMatchMedia('(max-width: 768px)');

  const checkoutLayout = useMemo(
    () => getCheckoutLayout(checkout.hostedLayoutType, checkoutType, isSmall),
    [checkout.hostedLayoutType, checkoutType, isSmall],
  );

  const [isProcessing, setIsProcessing] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [payment, setPayment] = useState(null);
  const [customer, setCustomer] = useState({});
  const [paymentGateway, setPaymentGateway] = useState(Object.keys(checkout.paymentMethods)[0]);
  const [paymentOption, setPaymentOption] = useState(getPaymentOption(checkout.paymentOptions));
  const [paymentMethod, setPaymentMethod] = useState(
    getPaymentMethod(checkout.paymentMethods, 'stripe'),
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [paymentError, setPaymentError] = useState('');
  const [formError, setFormError] = useState('');

  const [payWhatYouWantPrice, setPayWhatYouWantPrice] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [showCouponCodeField, setShowCouponCodeField] = useState(
    checkout.showCouponCodeFieldType === 'field' || !!query?.coupon || !!draftCheckout?.coupon, // if using type 'field', show field immediately or if coupon is present
  );

  const { form, setForm } = useCheckoutForm({
    checkout,
    product,
    query,
    countryCode,
    onPayWhatYouWantPriceChange: setPayWhatYouWantPrice,
    draftCheckout,
    isPreview,
  });

  const [address, setAddress] = useState({
    mode: getAddressMode({
      shippingAddressCollection: checkout.shippingAddressCollection,
      billingAddressCollection: checkout.billingAddressCollection,
    }),
    name: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    },
  });

  const [visitId, setVisitId] = useState('');
  const [trackedEvents, setTrackedEvents] = useState([]);
  const [state, setState] = useState(getInitialCheckoutState(query));

  const [layoutId, setLayoutId] = useState(null);
  const [freeTrialDuration, setFreeTrialDuration] = useState(1);
  const [freeTrialDurationType, setFreeTrialDurationType] = useState('day');
  const [isChecked, setIsChecked] = useState(false);
  const [addressMode, setAddressMode] = useState(getAddressMode(checkout));
  const [orderNote, setOrderNote] = useState('');
  const [showPaymentOption, setShowPaymentOption] = useState(false);
  const [activeSidePanel, setActiveSidePanel] = useState(null);
  const [stripeAddress, setStripeAddress] = useState({});
  const hasFocusedRef = useRef(false);

  const formRef = useRef(null);
  const queryClient = useQueryClient();
  const [isUpsellRetry, setIsUpsellRetry] = useState(false);
  const router = useCheckoutRouter();

  const dynamicPrice = useMemo(() => {
    if (checkout.allowDynamicPrice && query.price) {
      return Number(query.price);
    }

    return null;
  }, [checkout, query.price]);

  const dynamicPlanIterations = useMemo(() => {
    if (checkout.productType !== 'subscription') {
      return null;
    }

    if (checkout.allowDynamicPlanIterations && query.no_of_payments) {
      return Number(query.no_of_payments);
    }

    return null;
  }, [checkout, query.no_of_payments]);

  useMemo(() => {
    setPayWhatYouWantPrice(formatPriceInput(form.payWhatYouWant, product.currency));
  }, [form.payWhatYouWant]);

  // update showCouponCodeField if changed
  useEffect(() => {
    setShowCouponCodeField(
      checkout.showCouponCodeFieldType === 'field' ||
        !!query?.coupon ||
        !!draftCheckout?.coupon ||
        !!coupon,
    );
  }, [checkout.showCouponCodeFieldType, query?.coupon, draftCheckout?.coupon, coupon]);

  // update paymentOption if changed
  useEffect(() => {
    if (!checkout.paymentOptions) {
      return;
    }

    const selectedPaymentOptionExists = !!checkout.paymentOptions.find(
      option => option.enabled === true && option._id === paymentOption._id,
    );

    if (paymentOption === null) {
      // if paymentOption is null, set the first enabled payment option
      setPaymentOption(getPaymentOption(checkout.paymentOptions));
    } else if (!selectedPaymentOptionExists) {
      // if the selected payment option no longer exists, set the first enabled payment option
      setPaymentOption(getPaymentOption(checkout.paymentOptions));
    } else {
      // set the currently selected payment option based on new data
      const updatedPaymentOption = checkout.paymentOptions.find(
        option => option._id === paymentOption._id,
      );

      setPaymentOption(updatedPaymentOption);
    }
  }, [checkout.paymentOptions]);

  async function handleSetSubmission(submission) {
    setSubmission(submission);
    setCustomer(submission.customer);

    const sessionId = getSessionId();

    // Update the submission in the query cache
    queryClient.setQueryData(submissionKey(submission._id, sessionId), submission);
  }

  function handleSetPayment(payment) {
    // Update the payment in the state
    setPayment(payment);
    setCustomer(payment.customer);

    const sessionId = getSessionId();

    // Update the payment in the query cache
    queryClient.setQueryData(paymentKey(payment._id, sessionId), payment);
  }

  function resetForm() {
    setForm({
      name: '',
      email: '',
      discountCode: '',
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: '',
    });
    setPayWhatYouWantPrice(null);
    setCoupon(null);
    setPayment(null);
    setIsProcessing(false);
    setPaymentError('');
    setFormError('');
    setPaymentGateway(Object.keys(checkout.paymentMethods)[0]);
    setPaymentMethod(getPaymentMethod(checkout.paymentMethods, 'stripe'));
    setTrackedEvents([]);
    setState(checkoutStates.INITIAL);
    hasFocusedRef.current = false;
  }

  function removeCoupon() {
    setCoupon(null);
  }

  function restartPayment({ isConfirmation = false } = {}) {
    // same as resetForm except we keep the paymentIntent
    // which is needed for redirect based payment methods
    // if the payment fails and checkout gets reloaded, it will have a new payment intent created on server
    // if we then use resetForm (on clicking Try again), the payment intent is removed from the checkout
    // and the purchase creates a new payment intent somehow, which results in orphan purchases
    // @todo we can combine this again once we launch payment element without payment intents

    setForm({
      name: '',
      email: '',
      discountCode: '',
      fields:
        checkout.fields && mapFieldsToState(checkout.fields, query, countryCode, draftCheckout),
      variants: product.variants && getVariantsFormFields(product.variants, query, draftCheckout),
      ticketGroups:
        checkout.ticketGroups &&
        getTicketGroupsFormState(checkout.ticketGroups, query, draftCheckout),
      payWhatYouWant: '',
    });
    setPayWhatYouWantPrice(null);
    setCoupon(null);
    setPayment(null);
    setIsProcessing(false);
    setPaymentError('');
    setFormError('');
    setPaymentGateway(Object.keys(checkout.paymentMethods)[0]);
    setPaymentMethod(getPaymentMethod(checkout.paymentMethods, 'stripe'));
    setTrackedEvents([]);
    setState(checkoutStates.INITIAL);
    hasFocusedRef.current = false;

    // navigate back from confirmation to the checkout/form page
    if (isConfirmation) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);

      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }

  const checkoutState = {
    page: checkout,
    checkout,
    product,
    seller,
    dynamicPrice,
    dynamicPlanIterations,
    isProcessing,
    setIsProcessing,
    submission,
    setSubmission: handleSetSubmission,
    payment,
    setPayment: handleSetPayment,
    paymentGateway,
    setPaymentGateway,
    paymentOption,
    setPaymentOption,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentError,
    setPaymentError,
    formError,
    setFormError,
    payWhatYouWantPrice,
    setPayWhatYouWantPrice,
    coupon,
    setCoupon,
    removeCoupon,
    form,
    setForm,
    address,
    setAddress,
    checkoutType,
    upsell,
    isUpsell,
    isUpsellRetry,
    setIsUpsellRetry,
    isTesting,
    isTestingPayments,
    isPreview,
    isMeasurer,
    visitId,
    setVisitId,
    trackedEvents,
    setTrackedEvents,
    state,
    setState,
    customer,
    setCustomer,
    query,
    params,
    resetForm,
    restartPayment,
    layoutId,
    setLayoutId,
    freeTrialDuration,
    setFreeTrialDuration,
    freeTrialDurationType,
    setFreeTrialDurationType,
    isChecked,
    setIsChecked,
    addressMode,
    setAddressMode,
    showCouponCodeField,
    setShowCouponCodeField,
    orderNote,
    setOrderNote,
    showPaymentOption,
    setShowPaymentOption,
    activeSidePanel,
    setActiveSidePanel,
    stripeAddress,
    setStripeAddress,
    hasFocusedRef,
    formRef,
    isScreenshot,
    countryCode,
    iframeId,
    checkoutLayout,
    isConfirmation,
    publicKey,
    publicKeyTest,
    draftCheckout,
  };

  // only attach to window on client
  if (!isProduction && typeof window !== 'undefined') {
    window.checkoutState = checkoutState;
  }

  return (
    <CheckoutStateContext.Provider value={checkoutState}>{children}</CheckoutStateContext.Provider>
  );
};

export default function useCheckoutState() {
  const context = React.useContext(CheckoutStateContext);

  if (context === undefined) {
    throw new Error('useCheckoutState must be used within a CheckoutStateProvider');
  }

  return context;
}

export { CheckoutStateContext, CheckoutStateProvider };
